import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const FALLBACK_REFERENCES = [
  'HR & FRU DANMARK',
  'P. Winther Jespersen',
  'A/S ANKER HANSEN & CO. A/S',
  'B&W ENERGY A/S',
  'BARSLUND A/S',
  'BRAVIDA',
  'HESTEFOLK',
  'F.J. POULSENS ANLÆGSGARTNERI A/S',
  'GRIBVAND SPILDEVAND A/S',
  'MT Højgaard',
  'LAURITZ HANNIBAL APS',
  'Wolff-Svendsen A/S',
  'NCC CONSTRUCTION DANMARK',
  'MOTORCYKEL ENTUSIASTER VEDBÆK HAVN'
];

export default function ReferencesPage() {
  const [references, setReferences] = useState(FALLBACK_REFERENCES);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/referencer?sort=order&order=asc')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch references');
        return res.json();
      })
      .then((data) => {
        if (data && data.items && data.items.length > 0) {
          const list = data.items
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((item) => item.name);
          setReferences(list);
        }
      })
      .catch(() => {
        // Keep fallback
      });
  }, []);

  return (
    <>
      <SEOHead
        title="Referencer | Gribskovsmeden ApS"
        description="Se vores referencer Gribskovsmeden ApS har stor erfaring i faget, og råder over dygtige medarbejder – hvilket tilmed betyder at du får professionel service når du handler med os, netop fordi vi ved præcis hvordan tingene skal gøres."
      />

      {/* Header Banner */}
      <div className="bg-stone-950 text-white py-14 md:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-xs text-amber-400 font-semibold uppercase tracking-wider mb-3">
              <Link to="/" className="hover:text-amber-300">Forside</Link>
              <span>/</span>
              <span>Referencer</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              Referencer
            </h1>

            <p className="mt-3 text-lg sm:text-xl text-stone-300 font-medium">
              Se vores referencer
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-stone-300">
              <a
                href="tel:+4548796816"
                className="flex items-center gap-2 hover:text-white bg-stone-900/80 border border-stone-800 px-4 py-2 rounded-lg whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-amber-500" aria-hidden="true"></i>
                <span>+45 48 79 68 16</span>
              </a>
              <a
                href="mailto:lars@gribskovsmeden.dk"
                className="flex items-center gap-2 hover:text-white bg-stone-900/80 border border-stone-800 px-4 py-2 rounded-lg whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope text-amber-500" aria-hidden="true"></i>
                <span>lars@gribskovsmeden.dk</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="text-amber-600 font-bold uppercase tracking-wider text-xs">
              Vores samarbejdspartnere og kunder
            </span>
            <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Se vores referencer
            </h2>
            <p className="mt-4 text-stone-600 leading-relaxed text-base">
              Gribskovsmeden ApS har stor erfaring i faget, og råder over dygtige medarbejder – hvilket tilmed betyder at du får professionel service når du handler med os, netop fordi vi ved præcis hvordan tingene skal gøres. Derfor kan du trygt kontakte os med opgaver.​​
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:flex sm:flex-wrap sm:items-center sm:gap-4">
            {references.map((ref, idx) => (
              <div
                key={idx}
                className="w-full sm:w-auto flex-initial flex flex-col sm:flex-row items-center text-center sm:text-left justify-center sm:justify-start gap-2 sm:gap-3.5 bg-stone-50 hover:bg-stone-100/90 border border-stone-200/90 rounded-xl p-3 sm:px-5 sm:py-4 transition-all duration-200 shadow-xs hover:shadow-sm group"
              >
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg bg-stone-900 text-amber-400 group-hover:bg-amber-500 group-hover:text-stone-950 transition-colors flex items-center justify-center text-xs sm:text-sm font-bold shrink-0">
                  <i className="fa-solid fa-check" aria-hidden="true"></i>
                </div>
                <div className="min-w-0 w-full sm:w-auto">
                  <div className="font-bold text-stone-900 text-xs sm:text-base leading-snug break-words sm:whitespace-nowrap line-clamp-2 sm:line-clamp-none">
                    {ref}
                  </div>
                  <div className="text-[10px] sm:text-xs text-stone-500 mt-0.5 font-medium">Kunde / Samarbejde</div>
                </div>
              </div>
            ))}
          </div>

          {/* Støttebanner and Badge Highlight */}
          <div className="mt-16 p-8 bg-stone-50 rounded-2xl border border-stone-200 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h3 className="mt-2 text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
                Støtte & engagement i lokalområdet
              </h3>
              <p className="text-stone-600 text-sm mt-1">
                Gribskovsmeden ApS bakker op om gode lokale initiativer og erhvervsliv.
              </p>
            </div>
            <img
              src="https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/st-ttebanner-mid2026-500x250.jpg"
              alt="Støttebanner"
              className="h-16 w-auto object-contain rounded-md border border-stone-300/80 shrink-0"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 bg-stone-900 text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold">
            Vil du også være en del af vores gode referencer?
          </h2>
          <p className="mt-2 text-stone-400 text-sm">
            Kontakt os i dag for en uforpligtende snak om dit bygge- eller smedeprojekt.
          </p>
          <div className="mt-6">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm rounded-lg transition-colors"
            >
              <span>Kontakt os nu</span>
              <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
